import classNames from 'classnames';
import { getLocalizedProperty, uuid } from '@loyalty/shared-common';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { ALL_ID, NameFormat } from '../../utils/naming';
import { GenericComponent } from '../../components-tree';
import { ProfileV2ComponentSkeleton } from './ProfileV2ComponentSkeleton';
import { ReactBootstrap } from '../../ReactBootstrap';
import { TranslationProvider } from '../TranslationProvider/TranslationProvider';
import { WidgetProps } from '../render-component';
import { formatProfileName } from '../format-profile-name';

export interface ProfileV2ComponentAttributes {
  showProfilePicture: boolean;
  showLevel: boolean;
  showProfileName: boolean;
  showCurrencyBalance: boolean;
  currencyId: string;
  profileNameFormat: NameFormat;
}

type ProfileComponent = GenericComponent<ProfileV2ComponentAttributes>;

const CLASSNAME = 'qlfeditor-profile';

type ProfileWidgetBackendProps = WidgetProps<ProfileComponent>;

interface CurrencyBalanceFrontendProps {
  id: string;
  image?: string;
  currencyName?: string;
  formattedValue: string;
  value: number;
}

interface ProfileWidgetFrontendProps {
  levelImage?: string;
  levelName?: string;
  profilePicture?: string;
  currencyBalances: CurrencyBalanceFrontendProps[];
  id: string;
  attributes: ProfileComponent['attributes'];
  profileName: string;
}

export function serializeProfileV2ComponentProps({
  state,
  options,
  context,
}: ProfileWidgetBackendProps): ProfileWidgetFrontendProps {
  const { profile, levels, programId, membership, currencies } = state;
  const currencyId = options.attributes.currencyId;
  const locale = context.i18n.locale;
  const availableCurrencyIds = new Set<string>(
    currencies.filter(currency => !currency.deletedAt).map(c => c.id),
  );

  const level = levels.find(({ id }) => id === membership.level?.current);
  const levelImage = getLocalizedProperty(level, 'image', locale);
  const levelName = getLocalizedProperty(level, 'name', locale);
  const currencyBalances: CurrencyBalanceFrontendProps[] = (
    currencyId !== ALL_ID
      ? profile.currencies.filter(
          balance => balance.programId === programId && balance.currencyId === currencyId,
        )
      : profile.currencies.filter(
          balance =>
            balance.programId === programId && availableCurrencyIds.has(balance.currencyId),
        )
  ).map(balance => {
    const currency = currencies.find(({ id }) => id === balance.currencyId);
    return {
      id: balance.id,
      image: getLocalizedProperty(currency, 'image', locale),
      currencyName: getLocalizedProperty(currency, 'name', locale),
      formattedValue: context.i18n.formatNumber(balance?.value ?? 0),
      value: balance?.value,
    };
  });

  const profileName = formatProfileName(profile, options.attributes.profileNameFormat, context);

  return {
    levelImage,
    levelName,
    currencyBalances,
    id: options.id || options.attributes.id || uuid(),
    attributes: options.attributes,
    profilePicture: profile.picture,
    profileName,
  };
}

function Component(props: ProfileWidgetFrontendProps) {
  const { profilePicture, id, attributes, levelImage, levelName, currencyBalances, profileName } =
    props;

  const { t } = useTranslation('participant');

  const {
    showProfilePicture,
    showLevel,
    showCurrencyBalance = true,
    showProfileName,
    class: className,
  } = attributes;

  return (
    <section
      className={classNames(CLASSNAME, className)}
      aria-label={t('aria.profile.label')}
      aria-description={t('aria.profile.description')}
    >
      {showProfilePicture && (
        <div className={`${CLASSNAME}__image`}>
          <img
            src={profilePicture || '/l/assets/loyalty/default_profile.jpg'}
            className={`${CLASSNAME}__image__profile`}
            alt={t('aria.profile.name', { name: profileName })}
          />
          {showLevel && levelImage && (
            <img
              src={levelImage}
              className={`${CLASSNAME}__image__level`}
              alt={t('aria.profile.level', { name: levelName })}
            />
          )}
        </div>
      )}
      {showProfileName && (
        <div className={`${CLASSNAME}__name`} aria-hidden>
          {profileName}
        </div>
      )}
      {showCurrencyBalance && (
        <div className={`${CLASSNAME}__points`}>
          {currencyBalances.map(balance => {
            return (
              <div
                className={`${CLASSNAME}__point`}
                key={balance.id}
                aria-label={t('aria.profile.balance', {
                  amount: balance.value,
                  name: balance.currencyName,
                })}
              >
                {balance.image && (
                  <img aria-hidden alt="" src={balance.image} width="36px" height="36px" />
                )}
                <span aria-hidden>{balance.formattedValue}</span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function ProfileV2Component(props: ProfileWidgetFrontendProps) {
  return (
    <TranslationProvider loader={<ProfileV2ComponentSkeleton />}>
      <Component {...props} />
    </TranslationProvider>
  );
}

export function ProfileV2Renderer(props: ProfileWidgetBackendProps, document?: Document): string {
  const reactProps = serializeProfileV2ComponentProps(props);
  return renderToString(
    <ReactBootstrap
      component="ProfileV2Component"
      props={reactProps}
      document={document}
      componentType="profile-v2"
    />,
  );
}
